import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import RunAnalysisTemplateModal from 'analysis-tool/create-analysis-modal/RunAnalysisTemplateModal';
import BenchlingConfig from 'BenchlingConfig';
import DataCatalogItemDetails, {DataCatalogItemActionIcon} from 'data-catalog/DataCatalogItemDetails';
import DataCatalogOrigin from 'data-catalog/DataCatalogOrigin';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {updateFile} from 'files/network/FileDAO';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {fileStatusDisplay} from 'instruments/connection-files/ConnectionFilesStatus';
import {renderFilePreview} from 'pipelines/files/ScratchFilePaneContent';
import {isPipelineCapabilityEnabled} from 'pipelines/PipelineCapabilities';
import {Title} from 'ui-library/Headers';
import runModal from 'ui-library/runModal';
import {formatBytes} from 'util/NameUtil';
import removeNulls from 'util/removeNulls';

import {FileViewQuery} from './__generated/FileViewQuery';

type File = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;

interface FileDetailsProps {
  file: File;
  reload: () => void;
}

export default function FileDetails({file, reload}: FileDetailsProps): JSX.Element {
  const extraColumns = [
    <>
      <Title>Status</Title>
      {fileStatusDisplay(file, false)}
    </>,
    <>
      <Title>Benchling File ID</Title>
      {file.displayId}
    </>,
    <>
      <Title>Size</Title>
      {file.scratchFile.size ? formatBytes(file.scratchFile.size) : ''}
    </>,
    <>
      <Title>Origin</Title>
      <DataCatalogOrigin originType={file.originType} lineageParent={file.lineageParent} />
    </>,
    <>
      <Title>Original File Name</Title>
      {file.scratchFile.filename}
    </>,
  ];

  const actionIcons: Array<DataCatalogItemActionIcon | null> = [
    isAnalysisTemplatesEnabled() &&
    BenchlingConfig.ENABLE_INSIGHTS_LABS &&
    isPipelineCapabilityEnabled('CONNECT')
      ? {
          title: 'Run analysis template',
          icon: 'analytics_analysis',
          onClick: () =>
            runModal(RunAnalysisTemplateModal, {
              variableDefaults: {FILE: file.id},
              initialAnalysisName: `Analyze ${file.name}`,
            }),
        }
      : null,
    {
      title: 'Download original file',
      icon: 'download',
      disabled: file.scratchFile.externalFileId == null || file.scratchFile.downloadUrl == null,
      onClick: async (): Promise<void> => {
        const signedURL = await performSignedFileQuery(
          file.scratchFile.externalFileId!,
          file.scratchFile.downloadUrl!,
          file.id
        );
        if (signedURL) {
          window.open(signedURL, '_blank', 'noopener noreferrer');
        }
      },
    },
  ];

  return (
    <DataCatalogItemDetails
      dataCatalogItem={file}
      extraColumns={extraColumns}
      tagsEmptyMessage="This file has no custom fields."
      onChange={async (updateParams: {
        id: string;
        name?: string;
        folderId?: string;
        customFields?: {[name: string]: string};
      }): Promise<void> => {
        await updateFile(updateParams);
        reload();
      }}
      actionIcons={removeNulls(actionIcons)}
    >
      <div className={css(styles.filePreview)}>{renderFilePreview(file.scratchFile)}</div>
    </DataCatalogItemDetails>
  );
}

const styles = StyleSheet.create({
  filePreview: {
    paddingTop: 8,
    // It's important for some file previews that grow, to have a size set, but others that have their own
    // height can still go beyond this since we don't set overflow: hidden/scroll (e.g. csv can be quite tall.)
    height: 400,
  },
});
